import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormInput } from "../../Components/Form";
import { CompanyLogo, ToastMessage } from "../../Components/Utils";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { useChangePasswordMutation } from "../../stores/api/auth.api";
import { handleError } from "../../stores/components/handleError";

const ChangePassword = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [formData, setFormData] = useState({ password: "", confirm_password: "" });
  const [submitted, setSubmitted] = useState(false);
  const [formExiting, setFormExiting] = useState(false);
  const [successEntered, setSuccessEntered] = useState(false);

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password || !formData.confirm_password) {
      ToastMessage({ type: "error", title: "Validation error!", message: "Both fields are required." });
      return;
    }
    if (formData.password !== formData.confirm_password) {
      ToastMessage({ type: "error", title: "Validation error!", message: "Passwords do not match." });
      return;
    }
    if (formData.password.length < 6) {
      ToastMessage({ type: "error", title: "Validation error!", message: "Password must be at least 6 characters." });
      return;
    }

    try {
      await changePassword({ code, ...formData }).unwrap();

      setFormExiting(true);
      setTimeout(() => {
        setSubmitted(true);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setSuccessEntered(true))
        );
      }, 300);
    } catch (error) {
      handleError(error, "Failed to change password. The link may have expired.");
    }
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="rounded-[2rem] bg-white/95 p-5 shadow-2xl shadow-black/10 ring-1 ring-slate-200/80 dark:bg-slate-900/95 dark:shadow-slate-950/20 dark:ring-slate-800/70">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-16 w-auto min-w-16 max-w-[200px] items-center justify-center rounded-3xl bg-slate-100 dark:bg-slate-800 px-4 shadow-inner shadow-slate-200/40 dark:shadow-slate-950/30">
            <CompanyLogo className="h-10 w-auto max-w-full" />
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.35em] text-slate-500 dark:text-slate-400">Admin Portal</p>
          <h2 className="mt-4 text-3xl font-semibold text-slate-900 dark:text-white">Set new password</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">Enter your new password below.</p>
        </div>

        <div className="relative">
          {!submitted && (
            <div
              className={`transition-all duration-300 ease-in-out ${
                formExiting
                  ? "opacity-0 -translate-y-3 scale-95 pointer-events-none"
                  : "opacity-100 translate-y-0 scale-100"
              }`}
            >
              <form className="space-y-3" onSubmit={handleSubmit}>
                <FormInput
                  name="password"
                  value={formData.password}
                  onChange={(val) => setField("password", val)}
                  label="New Password"
                  placeholder="Min. 6 characters"
                  type="password"
                  required
                  className="p-1"
                />
                <FormInput
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={(val) => setField("confirm_password", val)}
                  label="Confirm Password"
                  placeholder="Repeat your password"
                  type="password"
                  required
                  className="p-1"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-green-500 px-4 py-2.5 text-sm font-semibold text-white shadow-green-500/30 transition duration-200 hover:bg-green-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                    </svg>
                  )}
                  {isLoading ? "Saving..." : "Set new password"}
                </button>
              </form>
            </div>
          )}

          {submitted && (
            <div
              className={`transition-all duration-500 ease-out ${
                successEntered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <div className="rounded-3xl bg-slate-100/90 p-6 text-slate-900 dark:bg-slate-900/90 dark:text-slate-200">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 dark:bg-green-500/10">
                  <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="font-semibold text-slate-900 dark:text-white mb-3">Password updated!</p>
                <p>Your password has been changed successfully. You can now sign in with your new password.</p>
                <button
                  type="button"
                  onClick={() => navigate(ROUTE_PATHS.LOGIN)}
                  className="mt-6 inline-flex items-center justify-center rounded-2xl bg-green-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-400 transition duration-200"
                >
                  Back to login
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-3xl bg-slate-100/80 p-4 text-center text-sm text-slate-600 dark:bg-slate-900/80 dark:text-slate-400">
          If you need help signing in, contact your administrator for support.
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
